import { Box, Button } from '@material-ui/core'
import get from 'lodash/get'
import truncate from 'lodash/truncate'
import moment from 'moment'
import { styled } from 'opensolar-ui'
import React from 'react'
import { Show, TopToolbar, useLocale, useShowController } from 'react-admin'
import { useTaxIdentifierLabel } from 'resources/wallet/useTaxIdentifierLabel'
import { AccessRightsType } from 'types/global'
import { currencySymbolForCurrencyCode, formatCurrencyWithSymbol, taxNameForCountryIso2 } from '../../util/misc'
import { useTransactionTaxInfo } from './useTransactionTaxInfo'
import { amountForInvoice, formatTaxRatePercent } from './walletTransactionAmountDisplay'

type PropTypes = {
  accessRights?: AccessRightsType
  record?: unknown
  basePath?: string
  id?: string
  resource?: string
  [key: string]: unknown
}

const TransactionWrapper = styled('div', { name: 'TransactionWrapper' })(({ theme }) => ({
  marginTop: '1rem',
}))

const Divider = styled('hr', { name: 'WalletTransactionDivider' })(({ theme }) => ({
  border: 'none',
  borderTop: `1px solid ${theme.palette.grey[400]}`,
  margin: '24px 0',
}))

const SummaryTable = styled('table', { name: 'WalletTransactionSummaryTable' })(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[400]}`,
  borderCollapse: 'collapse',
  minWidth: '250px',
}))

const SummaryTableCell = styled('td', { name: 'WalletTransactionSummaryTableCell' })(({ theme }) => ({
  padding: '8px 12px',
  minWidth: '120px',
  border: `1px solid ${theme.palette.grey[400]}`,
  textAlign: 'left',
}))

const SummaryTableCellRight = styled(SummaryTableCell, { name: 'WalletTransactionSummaryTableCellRight' })({
  textAlign: 'right',
})

const SummaryTableTotalRow = styled('tr', { name: 'WalletTransactionSummaryTableTotalRow' })(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}))

const LineItemTable = styled('table', { name: 'WalletTransactionLineItemTable' })(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[400]}`,
  borderCollapse: 'collapse',
  width: '100%',
}))

const LineItemTableHeaderCell = styled('th', { name: 'WalletTransactionLineItemTableHeaderCell' })(({ theme }) => ({
  padding: '8px 12px',
  border: `1px solid ${theme.palette.grey[400]}`,
  textAlign: 'left',
  backgroundColor: theme.palette.grey[100],
  fontWeight: 600,
}))

const LineItemTableHeaderCellRight = styled(LineItemTableHeaderCell, {
  name: 'WalletTransactionLineItemTableHeaderCellRight',
})({
  textAlign: 'right',
})

const LineItemTableCell = styled('td', { name: 'WalletTransactionLineItemTableCell' })(({ theme }) => ({
  padding: '8px 12px',
  border: `1px solid ${theme.palette.grey[400]}`,
  textAlign: 'left',
}))

const LineItemTableCellRight = styled(LineItemTableCell, { name: 'WalletTransactionLineItemTableCellRight' })({
  textAlign: 'right',
})

const AccountHeading = styled('div', { name: 'WalletTransactionAccountHeading' })({
  fontWeight: 700,
  marginBottom: '16px',
})

const AccountDetails = styled('div', { name: 'WalletTransactionAccountDetails' })({
  textAlign: 'left',
  '& > div': {
    marginBottom: '8px',
  },
})

const ProductName = styled('div', { name: 'ProductName' })({
  fontWeight: 700,
})

const DescriptionSecondary = styled('div', { name: 'DescriptionSecondary' })({
  color: '#757575',
  fontWeight: 400,
})

const Notes = styled('div', { name: 'Notes' })({
  color: '#757575',
  fontWeight: 400,
  fontStyle: 'italic',
})

const Subtotal = ({ record }) => {
  const locale = useLocale()
  const countryIso2 = record.org.country_iso2
  const reverseChargeText = useTransactionTaxInfo(countryIso2)
  const taxRateLabel = formatTaxRatePercent(record.tax_combined, record.amount_ex_tax, record.amount_inc_tax)
  const taxName = taxNameForCountryIso2(countryIso2).toUpperCase()

  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <div>
        {record.transaction_type === 'purchase' && reverseChargeText}
        <TransactionNotes record={record} />
      </div>
      <SummaryTable>
        <tbody>
          <tr>
            <SummaryTableCell>Subtotal</SummaryTableCell>
            <SummaryTableCellRight>
              {formatCurrencyWithSymbol(
                amountForInvoice(record.amount_ex_tax, record.transaction_type),
                currencySymbolForCurrencyCode(record.currency),
                locale,
                2
              )}
            </SummaryTableCellRight>
          </tr>
          <tr>
            <SummaryTableCell>
              {taxName} {taxRateLabel}
            </SummaryTableCell>
            <SummaryTableCellRight>
              {formatCurrencyWithSymbol(
                amountForInvoice(record.tax_combined, record.transaction_type),
                currencySymbolForCurrencyCode(record.currency),
                locale,
                2
              )}
            </SummaryTableCellRight>
          </tr>
          <SummaryTableTotalRow>
            <SummaryTableCell>Total</SummaryTableCell>
            <SummaryTableCellRight>
              {formatCurrencyWithSymbol(
                amountForInvoice(record.amount_inc_tax, record.transaction_type),
                record.currency,
                locale,
                2
              )}
            </SummaryTableCellRight>
          </SummaryTableTotalRow>
        </tbody>
      </SummaryTable>
    </Box>
  )
}

const PurchaseInfo = ({ record, lineItem }) => {
  if (record.transaction_type !== 'purchase' || !lineItem) return <></>
  const hasProjectId = lineItem.project_id != null
  const hasOtherDetails = Boolean(lineItem.line_item_other_details)
  if (!hasProjectId && !hasOtherDetails) return <></>
  return (
    <>
      {hasProjectId && <DescriptionSecondary>Project ID: #{lineItem.project_id}</DescriptionSecondary>}
      {hasOtherDetails && <DescriptionSecondary>{lineItem.line_item_other_details}</DescriptionSecondary>}
    </>
  )
}

const TransactionNotes = ({ record }) => {
  const notes = truncate(get(record, 'notes', ''), { length: 100 })
  if (!notes) return <></>
  return <Notes>{notes}</Notes>
}

const TaxIdentifier = ({ record }) => {
  const countryIso2 = record.org.country_iso2
  const tax_identifier_label = useTaxIdentifierLabel(record.currency, countryIso2)

  return record.tax_identifier ? (
    <>
      <strong>{tax_identifier_label}:</strong> {record.tax_identifier}
    </>
  ) : (
    <></>
  )
}

const PaidAmount = ({ record }) => {
  const locale = useLocale()

  if (!record) return null
  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <SummaryTable>
        <tbody>
          <tr>
            <SummaryTableCell>Paid Amount</SummaryTableCell>
            <SummaryTableCellRight>
              {formatCurrencyWithSymbol(
                amountForInvoice(record.amount_inc_tax, record.transaction_type),
                currencySymbolForCurrencyCode(record.currency),
                locale,
                2
              )}
            </SummaryTableCellRight>
          </tr>
          <tr>
            <SummaryTableCell>Balance due</SummaryTableCell>
            <SummaryTableCellRight>
              {formatCurrencyWithSymbol(
                record.amount_inc_tax - record.amount_inc_tax,
                currencySymbolForCurrencyCode(record.currency),
                locale,
                2
              )}
            </SummaryTableCellRight>
          </tr>
        </tbody>
      </SummaryTable>
    </Box>
  )
}

const DESCRIPTION_BY_TYPE = {
  gift: 'Gift',
  deposit: 'Deposit',
  adjustment: 'Adjustment',
  refund: 'Refund',
  other: 'Other',
}
const InvoiceDescription = ({ record }) => {
  const locale = useLocale()
  const lineItems = record.transaction_line_items ?? []
  const isPurchase = record.transaction_type === 'purchase'
  const showQuantityOne = !isPurchase

  if (!record) return null

  if (lineItems.length === 0) {
    return null
  }

  return (
    <LineItemTable>
      <thead>
        <tr>
          <LineItemTableHeaderCell>Description</LineItemTableHeaderCell>
          <LineItemTableHeaderCellRight>Quantity</LineItemTableHeaderCellRight>
          <LineItemTableHeaderCellRight>Unit Price</LineItemTableHeaderCellRight>
          <LineItemTableHeaderCellRight>Amount</LineItemTableHeaderCellRight>
        </tr>
      </thead>
      <tbody>
        {lineItems.map((lineItem, index) => {
          const descriptionLabel = lineItem.product_purchased_name || DESCRIPTION_BY_TYPE[record.transaction_type]

          return (
            <tr key={lineItem.id ?? index}>
              <LineItemTableCell as="th" scope="row">
                <ProductName>{descriptionLabel}</ProductName>
                <PurchaseInfo record={record} lineItem={lineItem} />
              </LineItemTableCell>
              <LineItemTableCellRight>{showQuantityOne ? 1 : lineItem.units}</LineItemTableCellRight>
              <LineItemTableCellRight>
                {formatCurrencyWithSymbol(
                  amountForInvoice(lineItem.amount_ex_tax, record.transaction_type),
                  currencySymbolForCurrencyCode(record.currency),
                  locale,
                  2
                )}
              </LineItemTableCellRight>
              <LineItemTableCellRight>
                {formatCurrencyWithSymbol(
                  amountForInvoice(lineItem.amount_ex_tax, record.transaction_type),
                  currencySymbolForCurrencyCode(record.currency),
                  locale,
                  2
                )}
              </LineItemTableCellRight>
            </tr>
          )
        })}
      </tbody>
    </LineItemTable>
  )
}

const BillTo = ({ record }: { record: any }) => {
  if (!record) return null
  const { org } = record
  const isTaxInvoice = transactionIsTaxInvoice(record?.transaction_type)
  const numberLabel = isTaxInvoice ? 'Invoice number:' : 'Receipt number:'
  const addressLine2 = [org?.locality, org?.state, org?.zip].filter(Boolean).join(' ')
  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <AccountHeading>Account</AccountHeading>
        <AccountDetails>
          <div>{record.org_name}</div>
          {org?.address && <div>{org.address}</div>}
          {addressLine2 && <div>{addressLine2}</div>}
          {record.tax_identifier && (
            <div>
              <TaxIdentifier record={record} />
            </div>
          )}
        </AccountDetails>
      </div>
      <SummaryTable>
        <tbody>
          <tr>
            <SummaryTableCell>{numberLabel}</SummaryTableCell>
            <SummaryTableCell>{record.id}</SummaryTableCell>
          </tr>
          <tr>
            <SummaryTableCell>Date:</SummaryTableCell>
            <SummaryTableCell>{moment(record.created_date).format('LL')}</SummaryTableCell>
          </tr>
        </tbody>
      </SummaryTable>
    </Box>
  )
}

const InvoiceHeading = styled('h1', { name: 'WalletTransactionInvoiceHeading' })({
  margin: 0,
  marginBottom: '16px',
})

const OpenSolarDetails = styled('div', { name: 'WalletTransactionOpenSolarDetails' })({
  '& > div': {
    marginBottom: '8px',
  },
})

const InvoiceHeadingWithRecord = ({ record }: { record: any }) => {
  if (!record) return null
  const isTaxInvoice = transactionIsTaxInvoice(record?.transaction_type)
  const heading = isTaxInvoice ? 'Tax Invoice' : 'Receipt'
  return <InvoiceHeading>{heading}</InvoiceHeading>
}

const transactionIsTaxInvoice = (transactionType: string) =>
  transactionType === 'purchase' || transactionType === 'reversal'

const PRINT_STYLES = `
  @media print {
    body * { visibility: hidden; }
    .wallet-transaction-invoice,
    .wallet-transaction-invoice * { visibility: visible; }
    .wallet-transaction-invoice {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      margin: 0;
      padding: 0;
      background: white;
    }
  }
`

const PostShowActions = () => (
  <TopToolbar>
    <Button variant="outlined" color="primary" size="small" onClick={() => window.print()}>
      Print to PDF
    </Button>
  </TopToolbar>
)

export const WalletTransactionEdit: React.FC<PropTypes> = (props) => {
  const controllerProps = useShowController(props as { basePath: string; id: string; resource: string })
  const { record, loading } = controllerProps

  if (loading) {
    return (
      <TransactionWrapper style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <span>Loading…</span>
      </TransactionWrapper>
    )
  }

  if (!record) {
    return <div>No record found</div>
  }

  const showProps = { ...props, ...controllerProps }

  return (
    <TransactionWrapper>
      <style>{PRINT_STYLES}</style>
      {/*@ts-ignore*/}
      <Show actions={<PostShowActions />} {...showProps} title="Wallet Transaction">
        <div className="wallet-transaction-invoice">
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <InvoiceHeadingWithRecord record={record} />
              <OpenSolarDetails>
                <div>OpenSolar Pty Ltd</div>
                <div>Level 1/477 Pitt Street,</div>
                <div>Haymarket NSW 2000</div>
                <div>
                  <strong>ABN:</strong> 82 621 679 632
                </div>
              </OpenSolarDetails>
            </div>
            <img
              src={`${window.PUBLIC_URL}/images/logo.svg`}
              alt="OpenSolar"
              style={{ maxWidth: '16rem', marginTop: 32, marginRight: 32, flexShrink: 0 }}
            />
          </Box>
          <Divider />
          <BillTo record={record} />
          <Divider />
          <InvoiceDescription record={record} />
          <br />

          <Subtotal record={record} />
          <br />
          <PaidAmount record={record} />
        </div>
      </Show>
    </TransactionWrapper>
  )
}
export default WalletTransactionEdit
